// Sprint 6 — DateRangePicker riusabile (DEBT-027).
// Wrapping di react-day-picker con preset rapidi e stile coerente con i token Audiq.

import { useEffect, useRef, useState } from 'react';
import { DayPicker, type DateRange } from 'react-day-picker';
import { it as itLocale } from 'react-day-picker/locale';
import dayjs from 'dayjs';
import 'dayjs/locale/it';

dayjs.locale('it');

export type Preset =
  | 'today'
  | 'yesterday'
  | 'last_7_days'
  | 'last_30_days'
  | 'this_month'
  | 'last_month';

const PRESETS: Array<{ value: Preset; label: string }> = [
  { value: 'today', label: 'Oggi' },
  { value: 'yesterday', label: 'Ieri' },
  { value: 'last_7_days', label: 'Ultimi 7 giorni' },
  { value: 'last_30_days', label: 'Ultimi 30 giorni' },
  { value: 'this_month', label: 'Questo mese' },
  { value: 'last_month', label: 'Mese scorso' },
];

export function presetRange(p: Preset): { from: Date; to: Date } {
  const now = dayjs();
  switch (p) {
    case 'today':
      return { from: now.startOf('day').toDate(), to: now.endOf('day').toDate() };
    case 'yesterday':
      return {
        from: now.subtract(1, 'day').startOf('day').toDate(),
        to: now.subtract(1, 'day').endOf('day').toDate(),
      };
    case 'last_7_days':
      return {
        from: now.subtract(7, 'day').startOf('day').toDate(),
        to: now.endOf('day').toDate(),
      };
    case 'last_30_days':
      return {
        from: now.subtract(30, 'day').startOf('day').toDate(),
        to: now.endOf('day').toDate(),
      };
    case 'this_month':
      return {
        from: now.startOf('month').toDate(),
        to: now.endOf('month').toDate(),
      };
    case 'last_month': {
      const last = now.subtract(1, 'month');
      return {
        from: last.startOf('month').toDate(),
        to: last.endOf('month').toDate(),
      };
    }
  }
}

export interface DateRangeValue {
  from: Date | null;
  to: Date | null;
}

interface Props {
  value: DateRangeValue;
  onChange: (next: DateRangeValue) => void;
  presets?: Preset[];
  /** Etichetta del bottone trigger quando vuoto. Default: "Periodo". */
  placeholder?: string;
}

export function DateRangePicker({
  value,
  onChange,
  presets = ['today', 'yesterday', 'last_7_days', 'last_30_days', 'this_month', 'last_month'],
  placeholder = 'Periodo',
}: Props) {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  // Click fuori chiude il popover.
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (!wrapperRef.current) return;
      if (!wrapperRef.current.contains(e.target as Node)) setOpen(false);
    };
    if (open) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const labelText = (() => {
    if (value.from && value.to) {
      return `${dayjs(value.from).format('DD MMM')} → ${dayjs(value.to).format('DD MMM YYYY')}`;
    }
    if (value.from) return `Da ${dayjs(value.from).format('DD MMM YYYY')}`;
    if (value.to) return `Fino ${dayjs(value.to).format('DD MMM YYYY')}`;
    return placeholder;
  })();

  const handleSelect = (range: DateRange | undefined) => {
    onChange({
      from: range?.from ?? null,
      to: range?.to ?? null,
    });
  };

  return (
    <div ref={wrapperRef} style={{ position: 'relative', display: 'inline-block' }}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="rounded-md border px-3 py-1.5 text-sm"
        style={{
          background: 'var(--paper)',
          borderColor: 'var(--line-strong)',
          color: 'var(--ink)',
          minWidth: 220,
          textAlign: 'start',
        }}
      >
        <span className="mono" style={{ fontSize: 11, color: 'var(--mute)', marginInlineEnd: 6 }}>
          📅
        </span>
        {labelText}
        {(value.from || value.to) && (
          <span
            onClick={(e) => {
              e.stopPropagation();
              onChange({ from: null, to: null });
            }}
            className="ms-2"
            style={{ color: 'var(--mute)', cursor: 'pointer' }}
            aria-label="Cancella filtro"
          >
            ×
          </span>
        )}
      </button>
      {open && (
        <div
          style={{
            position: 'absolute',
            top: 'calc(100% + 4px)',
            insetInlineStart: 0,
            zIndex: 30,
            background: 'var(--paper)',
            border: '1px solid var(--line)',
            borderRadius: 10,
            boxShadow: '0 8px 24px rgba(31,36,33,0.14)',
            padding: 12,
            display: 'flex',
            gap: 12,
            minWidth: 480,
          }}
        >
          <div
            className="space-y-1"
            style={{ minWidth: 140, paddingInlineEnd: 8, borderInlineEnd: '1px solid var(--line)' }}
          >
            {PRESETS.filter((p) => presets.includes(p.value)).map((p) => (
              <button
                key={p.value}
                type="button"
                onClick={() => {
                  const r = presetRange(p.value);
                  onChange({ from: r.from, to: r.to });
                  setOpen(false);
                }}
                className="block w-full rounded-md px-2 py-1.5 text-start text-sm hover:underline"
                style={{ color: 'var(--ink)' }}
              >
                {p.label}
              </button>
            ))}
          </div>
          <DayPicker
            mode="range"
            selected={
              value.from || value.to
                ? { from: value.from ?? undefined, to: value.to ?? undefined }
                : undefined
            }
            onSelect={handleSelect}
            locale={itLocale}
            weekStartsOn={1}
            numberOfMonths={1}
          />
        </div>
      )}
    </div>
  );
}
